'use client';

import type { PlantPhenologyClimateBand, PlantPhenologyLocationNote } from '@/data/plants';
import { useUserData } from '@/context/UserDataContext';

const CLIMATE_BAND_ZONES: Record<PlantPhenologyClimateBand, number[]> = {
  cold: [1, 2, 3, 4],
  cool: [5, 6],
  mild: [7, 8],
  warm: [9, 10],
  hot: [11, 12, 13],
  mildWinter: [9, 10, 11, 12, 13],
};

function noteMatchesZone(note: PlantPhenologyLocationNote, zone: number): boolean {
  if (note.zones && note.zones.length > 0) return note.zones.includes(zone);
  if (note.climateBand) return CLIMATE_BAND_ZONES[note.climateBand].includes(zone);
  return false;
}

function isGeneralNote(note: PlantPhenologyLocationNote): boolean {
  return (!note.zones || note.zones.length === 0) && !note.climateBand;
}

function getMatchingLocationNote(notes: PlantPhenologyLocationNote[] | undefined, zone: number | null | undefined) {
  if (!notes?.length) return null;

  const generalNote = notes.find(isGeneralNote);
  if (!zone) return generalNote ?? null;

  return notes.find(note => noteMatchesZone(note, zone)) ?? generalNote ?? null;
}

function LocationNoteContent({ note }: { note: PlantPhenologyLocationNote }) {
  return (
    <div className="mt-2 rounded-lg bg-light-sage px-3 py-2">
      <p className="font-body text-[11px] font-semibold uppercase tracking-wider text-sage">
        For your area
      </p>
      <p className="mt-0.5 font-body text-[13px] text-dark-text leading-snug">
        {note.note}
      </p>
    </div>
  );
}

export default function PhenologyLocationNote({
  notes,
}: {
  notes?: PlantPhenologyLocationNote[];
}) {
  const { zone } = useUserData();
  const matchingNote = getMatchingLocationNote(notes, zone);
  if (!matchingNote) return null;

  return <LocationNoteContent note={matchingNote} />;
}

export function PhenologyPhaseGroup({
  label,
  summary,
  cues,
  locationNotes,
}: {
  label: string;
  summary?: string;
  cues: string[];
  locationNotes?: PlantPhenologyLocationNote[];
}) {
  const { zone } = useUserData();
  const matchingNote = getMatchingLocationNote(locationNotes, zone);
  if (!summary && cues.length === 0 && !matchingNote) return null;

  return (
    <div>
      <p className="font-body font-semibold text-[13px] text-dark-text">
        {label}
      </p>
      {summary && (
        <p className="font-body text-[14px] text-sage leading-snug mt-1">
          {summary}
        </p>
      )}
      {cues.length > 0 && (
        <ul className="list-disc pl-5 mt-1 space-y-1">
          {cues.map((cue, index) => (
            <li key={`${cue}-${index}`} className="font-body text-[14px] text-sage leading-snug">
              {cue}
            </li>
          ))}
        </ul>
      )}
      {matchingNote && <LocationNoteContent note={matchingNote} />}
    </div>
  );
}
